import { Editor } from '@tiptap/react';
import { Node } from '@tiptap/pm/model';
import { CommentsOptions, DocumentThread, Thread } from './types';

export function findAllThreadsInDocument(
  editor: Editor,
  options: CommentsOptions
): DocumentThread[] {
  const threads: Array<DocumentThread> = [];

  editor.state.doc.descendants((node: Node, pos: number) => {
    if (node.type.name === options.blockThreadTypeName) {
      const id =
        node.attrs.id ||
        node.attrs['data-inline-thread-id'] ||
        node.attrs['data-block-thread-id'];

      if (id) {
        threads.push({
          id,
          type: 'block',
          pos,
          node,
        });
      }
    }

    node.marks.forEach((mark) => {
      if (mark.type.name === options.inlineThreadTypeName) {
        const id =
          mark.attrs.id ||
          mark.attrs['data-thread-id'] ||
          mark.attrs['data-inline-thread-id'];

        if (id) {
          const existing = threads.find(
            (t) => t.id === id && t.pos === pos && t.type === 'inline'
          );

          if (!existing) {
            threads.push({
              id,
              type: 'inline',
              pos,
              node,
              mark,
            });
          }
        }
      }
    });

    return true;
  });

  return threads;
}

export function removeUnusedThreads(
  editor: Editor,
  options: CommentsOptions,
  threadsToRemove: Array<DocumentThread>
) {
  let { tr } = editor.state;

  const sortedThreads = [...threadsToRemove].sort((a, b) => b.pos - a.pos);
  const blockThreads = sortedThreads.filter((t) => t.type === 'block');
  const inlineThreads = sortedThreads.filter((t) => t.type === 'inline');

  blockThreads.forEach(({ pos, node, id }) => {
    // Find all instances of this thread ID in the document
    tr.doc.descendants((descendantNode, descendantPos) => {
      if (
        descendantNode.type.name === options.blockThreadTypeName &&
        descendantNode.attrs.id === id
      ) {
        const nodeStart = descendantPos;
        const nodeEnd = descendantPos + descendantNode.nodeSize;

        const mappedStart = tr.mapping.map(nodeStart);
        const mappedEnd = tr.mapping.map(nodeEnd);

        if (descendantNode.content.size > 0) {
          tr = tr.replaceWith(mappedStart, mappedEnd, descendantNode.content);
        } else {
          tr = tr.delete(mappedStart, mappedEnd);
        }
      }
    });

    tr = tr.setMeta('addToHistory', false);
  });

  // Remove inline threads
  inlineThreads.forEach(({ id }) => {
    tr.doc.descendants((node, pos) => {
      node.marks.forEach((mark) => {
        if (
          mark.type.name === options.inlineThreadTypeName &&
          mark.attrs.id === id
        ) {
          const mappedFrom = tr.mapping.map(pos);
          const mappedTo = tr.mapping.map(pos + node.nodeSize);

          tr = tr.removeMark(mappedFrom, mappedTo, mark.type);
        }
      });
    });

    tr = tr.setMeta('addToHistory', false);
  });

  if (blockThreads.length > 0 || inlineThreads.length > 0) {
    tr = tr.setMeta('threadUpdate', true);
    editor.view.dispatch(tr);
  }
}

export function syncThreadsWithProvider(
  editor: Editor,
  options: CommentsOptions
) {
  if (!options.provider) return;

  const documentThreads: DocumentThread[] = findAllThreadsInDocument(
    editor,
    options
  );

  const providerThreads: Thread[] = options.provider.getThreads() || [];
  const providerThreadIds = new Set(providerThreads.map((t) => t.id));

  const threadsToRemove = documentThreads.filter(
    (docThread) => !providerThreadIds.has(docThread.id)
  );

  if (threadsToRemove.length > 0) {
    removeUnusedThreads(editor, options, threadsToRemove);
  }

  if (options.deleteUnreferencedThreads) {
    const documentThreadIds = new Set(documentThreads.map((t) => t.id));
    const unreferencedThreads = providerThreads.filter(
      (providerThread) =>
        !providerThread.markedAsDeleted &&
        !documentThreadIds.has(providerThread.id)
    );
    const restoredDocumentThread = providerThreads.filter(
      (providerThread) =>
        providerThread.markedAsDeleted &&
        documentThreadIds.has(providerThread.id)
    );

    unreferencedThreads.forEach((thread) => {
      options.provider?.markThreadAsDeleted({
        editor,
        id: thread.id,
        markedAsDeleted: true,
      });
    });

    restoredDocumentThread.forEach((thread) => {
      options.provider?.markThreadAsDeleted({
        editor,
        id: thread.id,
        markedAsDeleted: false,
      });
    });
  }
}
